import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import ModernVehicleSearch from '../../components/ui/ModernVehicleSearch'
import TyreSizeScanner from '../../components/search/TyreSizeScanner'
import {
  VehicleMake,
  VehicleModel
} from '../../services/vehicleApi'

const SearchPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchType, setSearchType] = useState<'vehicle' | 'size' | 'scan'>('vehicle')
  const [selectedVehicle, setSelectedVehicle] = useState<{make: VehicleMake; model: VehicleModel} | null>(null)
  const [sizeSearch, setSizeSearch] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)

  const handleVehicleSelect = (vehicle: {make: VehicleMake; model: VehicleModel}) => {
    setSelectedVehicle(vehicle)
  }

  const handleDropdownStateChange = (isOpen: boolean) => {
    setIsDropdownOpen(isOpen)
  }

  const handleSearch = () => {
    const params = new URLSearchParams()
    
    if (searchType === 'vehicle' && selectedVehicle) {
      params.append('make', selectedVehicle.make.name)
      params.append('model', selectedVehicle.model.name)
      params.append('makeId', selectedVehicle.make.id)
      params.append('modelId', selectedVehicle.model.id)
      if (selectedVehicle.model.default_tyre_size) {
        params.append('defaultTyreSize', selectedVehicle.model.default_tyre_size)
      }
    } else if (searchType === 'size' && sizeSearch) {
      params.append('size', sizeSearch)
    } else {
      return // Don't search if incomplete
    }

    navigate(`/search/results?${params.toString()}`)
  }

  const popularSizes = [
    '205/55R16',
    '215/55R17',
    '225/45R18',
    '225/60R18',
    '245/40R18',
    '195/65R15',
    '235/55R17'
  ]


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-indigo-600/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              Find Your Perfect
              <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Tyres
              </span>
            </h1>
            <p className="text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto">
              Search by vehicle or tyre size to discover compatible tyres from trusted shops near you
            </p>
          </motion.div>

          {/* Search Type Toggle */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="flex justify-center mb-8"
          >
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-2 shadow-lg border border-white/20">
              <div className="flex flex-wrap justify-center">
                <button
                  onClick={() => setSearchType('vehicle')}
                  className={`px-6 sm:px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-200 ${
                    searchType === 'vehicle'
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                    </svg>
                    <span>Search by Vehicle</span>
                  </div>
                </button>
                <button
                  onClick={() => setSearchType('size')}
                  className={`px-6 sm:px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-200 ${
                    searchType === 'size'
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v6a2 2 0 002 2h4a2 2 0 002-2V5z" />
                    </svg>
                    <span>Search by Size</span>
                  </div>
                </button>
                <button
                  onClick={() => setSearchType('scan')}
                  className={`px-6 sm:px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-200 ${
                    searchType === 'scan'
                      ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/25'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span>Scan Tyre</span>
                  </div>
                </button>
              </div>
            </div>
          </motion.div>

          {/* Main Search Interface */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="max-w-4xl mx-auto"
          >
            <AnimatePresence mode="wait">
              {searchType === 'scan' ? (
                <motion.div
                  key="scan"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-8"
                >
                  <TyreSizeScanner
                    onDetected={(size) => {
                      setSizeSearch(size)
                      setSearchType('size')
                      const params = new URLSearchParams()
                      params.append('size', size)
                      navigate(`/search/results?${params.toString()}`)
                    }}
                  />
                </motion.div>
              ) : searchType === 'vehicle' ? (
                <motion.div
                  key="vehicle"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-8"
                >
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Search by Vehicle</h2>
                    <p className="text-gray-600">Enter your vehicle details to find compatible tyres</p>
                  </div>

                  <ModernVehicleSearch
                    onVehicleSelect={handleVehicleSelect}
                    onDropdownStateChange={handleDropdownStateChange}
                    className="mb-8"
                  />

                  {/* Selected Vehicle Display */}
                  {selectedVehicle && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="mb-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {selectedVehicle.make.name} {selectedVehicle.model.name}
                          </h3>
                          <div className="flex items-center space-x-4 mt-1">
                            <span className="text-sm text-gray-600">
                              {selectedVehicle.model.year_from || '?'} - {selectedVehicle.model.year_to || 'Present'}
                            </span>
                            {selectedVehicle.model.default_tyre_size && (
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                                Default Size: {selectedVehicle.model.default_tyre_size}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ) : (
                <motion.div
                  key="size"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/20 p-8"
                >
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Search by Tyre Size</h2>
                    <p className="text-gray-600">Enter your tyre size to find available options</p>
                  </div>

                  <div className="mb-6">
                    <div className="relative">
                      <input
                        type="text"
                        value={sizeSearch}
                        onChange={(e) => setSizeSearch(e.target.value)}
                        placeholder="e.g., 205/55R16"
                        className="w-full pl-16 pr-4 py-6 text-xl bg-white border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                      <div className="absolute left-6 top-1/2 transform -translate-y-1/2 text-gray-400">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v6a2 2 0 002 2h4a2 2 0 002-2V5z" />
                        </svg>
                      </div>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Format: Width/Profile R Diameter (e.g., 205/55R16)
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-3">Popular sizes:</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {popularSizes.map(size => (
                        <button
                          key={size}
                          onClick={() => setSizeSearch(size)}
                          className="p-3 bg-gray-100 hover:bg-blue-50 border border-gray-300 hover:border-blue-400 rounded-xl text-sm font-medium text-gray-900 transition-colors"
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>


          {/* Search Button - Hidden when dropdown is open */}
          <AnimatePresence>
            {!isDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ delay: 0.5 }}
                className="text-center mt-8"
              >
                <button
                  onClick={handleSearch}
                  disabled={
                    (searchType === 'vehicle' && !selectedVehicle) ||
                    (searchType === 'size' && !sizeSearch) ||
                    searchType === 'scan'
                  }
                  className="group relative bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-12 py-6 rounded-2xl font-semibold text-xl hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  <span className="flex items-center space-x-3">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <span>Find My Tyres</span>
                  </span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  )
}

export default SearchPage
